import React, { useCallback, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import Form from '@atlaskit/form';
import { Box, Stack, Text, xcss, Flex } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import type { DocNode } from '@atlaskit/adf-schema';

import { fg } from '@confluence/feature-gating';

import { messages } from '../../messages';
import { saveJiraConfigDetailsToLocalStorage } from '../../utils/localStorageHelpers';
import { useGetInitialDataForSingleIssueCreateForm } from '../../services/useGetInitialDataForSingleIssueCreateForm';
import { useIssueCreateSidePanelActions } from '../../providers/IssueCreateSidePanelContextProvider';
import { useCreateSingleWorkItem } from '../../services/useCreateSingleWorkItem';
import { IssueTypeSelector } from '../work-create-form-common/IssueTypeSelector';
import { SiteSelector } from '../work-create-form-common/SiteSelector';
import { ProjectSelector } from '../work-create-form-common/ProjectSelector';

import { SingleWorkCreateFormFooter } from './SingleWorkCreateFormFooter';
import { TextField } from '../work-create-form-common/TextField';
import { RichTextField } from '../work-create-form-common/RichTextField';

const singleWorkCreateFormStyles = xcss({
	flexGrow: 1,
	overflowY: 'auto',
});

const singleWorkCreateFormContainerStyles = xcss({
	height: '100%',
});

// This is the view for both AI and non-AI single work create where users can edit and create individual issues
export const SingleWorkCreateForm = ({
	closePanel = () => {},
	summary: initialSummary = '',
	descriptionAdf: initialDescription,
}: {
	closePanel?: () => void;
	summary?: string;
	descriptionAdf?: DocNode;
}) => {
	const { setCurrentSiteSelection } = useIssueCreateSidePanelActions();
	const { formatMessage } = useIntl();

	// State for form fields
	const [summary, setSummary] = useState<string>(initialSummary);
	const [description, setDescription] = useState<DocNode | undefined>(initialDescription);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionError, setSubmissionError] = useState<string | null>(null);

	const {
		// Site picker data
		availableSites,
		selectedSite,
		isSitePickerLoading,
		// Project picker data
		selectedProject,
		optionsRecentProjects,
		optionsAllProjects,
		isProjectPickerLoading,
		// Issue type picker data
		selectedIssueType,
		optionsIssueTypes,
		isIssueTypePickerLoading,
		// Error handling
		error: formError,
		// Change handlers
		onSiteChange,
		onProjectChange,
		onIssueTypeChange,
	} = useGetInitialDataForSingleIssueCreateForm();

	const {
		createSingleWorkItemHandler,
		loading: isCreating,
		error: createError,
	} = useCreateSingleWorkItem();

	const handleSummaryChange = useCallback((value: string) => {
		setSummary(value);
	}, []);

	const handleDescriptionChange = useCallback((value: DocNode) => {
		setDescription(value);
	}, []);

	const handleSubmit = useCallback(async () => {
		if (isSubmitting || !selectedSite || !selectedProject || !selectedIssueType) return;

		try {
			setIsSubmitting(true);
			setSubmissionError(null);

			if (selectedSite) {
				void setCurrentSiteSelection(selectedSite.cloudId);
				saveJiraConfigDetailsToLocalStorage({
					selectedSite,
				});
			}

			await createSingleWorkItemHandler({
				cloudId: selectedSite.cloudId,
				issueBody: {
					fields: {
						summary,
						description,
						project: {
							id: selectedProject.id,
						},
						issuetype: {
							id: selectedIssueType.id,
						},
					},
				},
			});

			// Close the panel on successful submission
			closePanel();
		} catch (_error) {
			setSubmissionError(formatMessage(messages.singleWorkCreateFormJiraErrorText));
		} finally {
			setIsSubmitting(false);
		}
	}, [
		isSubmitting,
		selectedSite,
		selectedProject,
		selectedIssueType,
		setCurrentSiteSelection,
		summary,
		description,
		createSingleWorkItemHandler,
		closePanel,
		formatMessage,
	]);

	const onClose = useCallback(() => {
		closePanel();
	}, [closePanel]);

	const isSaveDisabled =
		isSubmitting ||
		isCreating ||
		isSitePickerLoading ||
		isProjectPickerLoading ||
		!selectedSite ||
		!summary.trim() ||
		!selectedProject ||
		!selectedIssueType; // Require all mandatory fields

	const error = submissionError || createError || formError;

	const singleWorkCreateFormBodyText = fg('confluence-issue-terminology-refresh')
		? messages.singleWorkCreateFormBodyTextIssueTermRefresh
		: messages.singleWorkCreateFormBodyText;

	return (
		<Flex
			testId="single-work-create-form-wrapper"
			direction="column"
			justifyContent="space-between"
			alignItems="center"
			xcss={singleWorkCreateFormContainerStyles}
		>
			<Box padding="space.200" xcss={singleWorkCreateFormStyles}>
				<Stack space="space.100">
					<Text color="color.text.subtlest">
						<FormattedMessage {...singleWorkCreateFormBodyText} />
					</Text>
					<Text size="small" color="color.text.accent.gray.bolder">
						Required fields are marked with an asterisk *
					</Text>
					{error && (
						<SectionMessage appearance="error" testId="singleWorkCreateFormErrorSectionMessage">
							{typeof error === 'string' ? (
								error
							) : (
								<FormattedMessage {...messages.singleWorkCreateFormJiraErrorText} />
							)}
						</SectionMessage>
					)}
					<Stack space="space.300">
						<Form onSubmit={handleSubmit}>
							{({ formProps }) => (
								<form
									id="single-work-create-form"
									data-testid="single-work-create-form"
									{...formProps}
								>
									<SiteSelector
										options={availableSites}
										value={selectedSite}
										onChange={onSiteChange}
										isLoading={isSitePickerLoading}
										isDisabled={isSitePickerLoading || isSubmitting || isCreating}
										isRequired
										label={formatMessage(messages.singleWorkCreateFormSite)}
									/>
									<ProjectSelector
										optionsAllProjects={optionsAllProjects}
										optionsRecentProjects={optionsRecentProjects}
										value={selectedProject}
										onChange={onProjectChange}
										isLoading={isProjectPickerLoading}
										isDisabled={
											isProjectPickerLoading || !selectedSite || isSubmitting || isCreating
										}
										label={formatMessage(messages.singleWorkCreateFormProject)}
										labelProjectSelectorRecentProjects={formatMessage(
											messages.singleWorkCreateFormProjectSelectorRecentProjects,
										)}
										labelProjectSelectorProjects={formatMessage(
											messages.singleWorkCreateFormProjectSelectorProjects,
										)}
										isRequired
									/>
									<IssueTypeSelector
										optionsIssueTypes={optionsIssueTypes}
										value={selectedIssueType}
										onChange={onIssueTypeChange}
										isLoading={isIssueTypePickerLoading}
										isDisabled={
											isIssueTypePickerLoading || !selectedProject || isSubmitting || isCreating
										}
										label={
											fg('confluence-issue-terminology-refresh')
												? formatMessage(
														messages.singleWorkCreateFormDefaultIssueTypeIssueTermRefresh,
													)
												: formatMessage(messages.singleWorkCreateFormDefaultIssueType)
										}
										isRequired
									/>
									<TextField
										label={formatMessage(messages.workCreateCommonSummaryLabel)}
										value={summary}
										onChange={handleSummaryChange}
										isRequired
										isDisabled={isSubmitting || isCreating}
									/>
									<RichTextField
										label={formatMessage(messages.workCreateCommonDescriptionLabel)}
										value={description}
										onChange={handleDescriptionChange}
										isDisabled={isSubmitting || isCreating}
									/>
								</form>
							)}
						</Form>
					</Stack>
				</Stack>
			</Box>
			<SingleWorkCreateFormFooter
				onClose={onClose}
				closePanel={closePanel}
				isSaveDisabled={isSaveDisabled}
			/>
		</Flex>
	);
};
